// The far-left icon rail: Explorer + Search at the top, Settings pinned to the
// bottom. Clicking a view sets it active (and toggles the sidebar when the
// already-active view is clicked, via setActivityView's store semantics).
// Parameterless, binds to the store.

import type { ReactNode } from "react";
import { Files, Search, Settings } from "lucide-react";
import { useIde } from "../store";
import type { ActivityView } from "../store";
import "./shell.css";

interface RailItem {
  view: ActivityView;
  label: string;
  hint: string;
  icon: ReactNode;
}

const ICON = { size: 20, strokeWidth: 1.75 } as const;

const TOP_ITEMS: RailItem[] = [
  { view: "explorer", label: "Explorer", hint: "⇧⌘E", icon: <Files {...ICON} /> },
  { view: "search", label: "Search", hint: "⇧⌘F", icon: <Search {...ICON} /> },
];

const SETTINGS_ITEM: RailItem = {
  view: "settings",
  label: "Settings",
  hint: ",",
  icon: <Settings {...ICON} />,
};

export function ActivityBar() {
  const activityView = useIde((s) => s.activityView);
  const sidebarVisible = useIde((s) => s.sidebarVisible);
  const setActivityView = useIde((s) => s.setActivityView);

  // An item reads as "active" only when its view is selected AND the panel it
  // drives is on screen, clicking the active view collapses the sidebar.
  function isActive(view: ActivityView): boolean {
    if (view !== activityView) return false;
    // Settings is a modal, not a sidebar panel, so it's active purely by view.
    if (view === "settings") return true;
    return sidebarVisible;
  }

  function renderItem(item: RailItem) {
    const active = isActive(item.view);
    return (
      <button
        key={item.view}
        type="button"
        className={`ab-item${active ? " ab-item--active" : ""}`}
        onClick={() => setActivityView(item.view)}
        title={`${item.label} (${item.hint})`}
        aria-label={item.label}
        aria-pressed={active}
      >
        <span className="ab-rail" aria-hidden="true" />
        {item.icon}
      </button>
    );
  }

  return (
    <nav className="ab" aria-label="Primary">
      <div className="ab-group">{TOP_ITEMS.map(renderItem)}</div>
      <div className="ab-group ab-group--bottom">{renderItem(SETTINGS_ITEM)}</div>
    </nav>
  );
}
